import CalendarIcon from "@redotech/redo-icons/calendar-date.svg";
import { getDateString } from "@redotech/util/date-format";
import { memo, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { TextSizeValue } from "../../theme/typography";
import { RedoButton, RedoButtonSize } from "../buttons/redo-button";
import { RedoDateRangePickerDropdown } from "./redo-date-range-picker-dropdown";

export interface RedoDateRangePickerProps {
  dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  setDateRange: (
    dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
  size?: RedoButtonSize;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  labelText?: string;
  allowNoDateOption?: boolean;
  dateStringOptions?: Pick<
    Intl.DateTimeFormatOptions,
    "year" | "month" | "day"
  >;
  dropdownStyle?: React.CSSProperties;
  popperModifiers?: any[];
  buttonContentClassName?: string;
  "data-testid"?: string;
}

const dateRangePickerButtonSizeToLabelSize: Record<
  RedoButtonSize,
  TextSizeValue
> = {
  xs: "xs",
  sm: "xs",
  md: "sm",
  lg: "sm",
  xl: "sm",
  "2xl": "sm",
  "3xl": "sm",
};

export const RedoDateRangePicker = memo(function RedoDateRangePicker({
  dateRange,
  setDateRange,
  size = "lg",
  minDate,
  maxDate,
  labelText,
  allowNoDateOption = false,
  dateStringOptions = {},
  dropdownStyle,
  popperModifiers,
  buttonContentClassName,
  "data-testid": dataTestId,
}: RedoDateRangePickerProps) {
  const buttonDisplay = dateRange
    ? `${getDateString(dateRange[0], dateStringOptions)} - ${getDateString(dateRange[1], dateStringOptions)}`
    : "Select dates";

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [pickerOpen, setPickerOpen] = useState(false);

  const labelSize: TextSizeValue = dateRangePickerButtonSizeToLabelSize[size];

  return (
    <>
      <Flex dir="column" gap="xs" justify="flex-start">
        {labelText && (
          <Text fontSize={labelSize} fontWeight="medium" textColor="secondary">
            {labelText}
          </Text>
        )}
        <RedoButton
          buttonContentClassName={buttonContentClassName}
          data-testid={dataTestId}
          hierarchy="secondary"
          IconLeading={CalendarIcon}
          onClick={(e) => {
            e.stopPropagation();
            setPickerOpen((open) => !open);
          }}
          ref={setDropdownAnchor}
          size={size}
          text={buttonDisplay}
        />
      </Flex>
      <RedoDateRangePickerDropdown
        allowNoDateOption={allowNoDateOption}
        anchorEl={dropdownAnchor}
        dateRange={dateRange}
        maxDate={maxDate}
        minDate={minDate}
        onClose={() => setPickerOpen(false)}
        open={pickerOpen}
        popperModifiers={popperModifiers}
        setDateRange={setDateRange}
        style={dropdownStyle}
      />
    </>
  );
});
